import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import CardDropdown from "components/common/CardDropdown";
import IconButton from "components/common/IconButton";
import SubtleBadge from "components/common/SubtleBadge";
import useDonationsPurposes from "hooks/donations/useDonationPurposes";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState } from "react";
import { Col, Dropdown, Row, Spinner } from "react-bootstrap";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { CreateNewPurposeModal } from "./CreateNewPurposeModal";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import { ViewPurposeModal } from "./ViewPurposeModal";
import { EditPurposeModal } from "./EditPurposeModal";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import { Empty } from "antd";

export const DonationsPuporses = () => {
  const {
    donationPurposes,
    loading,
    fetchDonationPurposeById,
    selectedPurpose,
    setSelectedPurpose,
    addDonationPurpose,
    updateDonationPurpose,
    deleteDonationPurpose,
    error,
  } = useDonationsPurposes();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [purposeToDelete, setPurposeToDelete] = useState(null);

  const handleViewPurpose = async (id) => {
    try {
      await fetchDonationPurposeById(id);
      setShowViewModal(true);
    } catch (error) {}
  };

  const handleUpdatePurpose = async (id) => {
    try {
      const purpose = await fetchDonationPurposeById(id);
      setSelectedPurpose(purpose);
      setShowUpdateModal(true);
    } catch (error) {}
  };

  const confirmDelete = (id) => {
    setPurposeToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    await deleteDonationPurpose(purposeToDelete);
  };

  const formatID = (id) => `${id.slice(0, 12)}...`;
  const formatText = (text) => `${text.slice(0, 20)}...`;

  const columns = [
    {
      accessorKey: "code",
      header: "Code",
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "text-xxs" },
      },
    },
    {
      accessorKey: "name",
      header: "Name",
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "text-xxs" },
      },
    },
    {
      accessorKey: "active",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();
        const style = {
          backgroundColor: value ? "#e8f7e3" : "#fbe9e7",
          color: value ? "#2e7d32" : "#c62828",
          padding: "3px 8px",
          borderRadius: "6px",
          fontSize: "0.65rem",
          border: value ? "1px solid #d5ebd0" : "1px solid #f5c6cb",
          cursor: "default",
          fontWeight: 600,
        };
        const content = value == true ? "Active" : "Disabled";
        return (
          <span className="py-2" style={style}>
            {content}
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ getValue }) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: 300 }}
          title={getValue()}
        >
          {getValue()}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "text-xxs" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item onClick={() => handleViewPurpose(item.id)}>
                View
              </Dropdown.Item>
              <Guard privilege={PRIV.UPDATE_PURPOSES}>
                <Dropdown.Item onClick={() => handleUpdatePurpose(item.id)}>
                  Update Status
                </Dropdown.Item>
              </Guard>
              <Guard privilege={PRIV.DELETE_PURPOSES}>
                <Dropdown.Divider as="div" />
                <Dropdown.Item
                  onClick={() => confirmDelete(item.id)}
                  className="text-danger"
                >
                  Delete
                </Dropdown.Item>
              </Guard>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: donationPurposes,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="hands-helping"
        title="Donations credit purposes"
        subtitle="Manage and monitor all donations credit purposes"
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      {donationPurposes && donationPurposes.length > 0 ? (
        <AdvanceTableProvider {...table}>
          <Row className="mb-2">
            <Col lg={4}>
              <AdvanceTableSearchBox />
            </Col>
            <Col lg={8} className="d-flex justify-content-end">
              <Guard privilege={PRIV.CREATE_PURPOSES}>
                <IconButton
                  variant="falcon-default"
                  size="sm"
                  icon="plus"
                  transform="shrink-3"
                  onClick={() => setShowAddModal(true)}
                  className="text-xs"
                >
                  <span className="ms-1">New Purpose</span>
                </IconButton>
              </Guard>
            </Col>
          </Row>

          <AdvanceTable
            headerClassName="bg-200 text-nowrap align-middle"
            rowClassName="btn-reveal-trigger text-nowrap align-middle"
            tableProps={{
              bordered: true,
              striped: true,
              className: "fs-10 mb-0 overflow-hidden",
            }}
          />

          <div className="mt-3">
            <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
          </div>
        </AdvanceTableProvider>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 200 }}
        >
          <Empty description="No donation purposes available" />
        </div>
      )}

      {/* View modal */}
      <ViewPurposeModal
        selectedPurpose={selectedPurpose}
        loading={loading}
        showModal={showViewModal}
        setShowModal={setShowViewModal}
      />

      {/* create new purpose modal */}
      <CreateNewPurposeModal
        open={showAddModal}
        setShowAddModal={setShowAddModal}
        addDonationPurpose={addDonationPurpose}
        existingPurposes={donationPurposes}
      />

      {/* edit purpose modal */}
      <EditPurposeModal
        showUpdateModal={showUpdateModal}
        setShowUpdateModal={setShowUpdateModal}
        selectedPurpose={selectedPurpose}
        updateDonationPurpose={updateDonationPurpose}
      />

      {/* confirm delete modal */}
      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        itemName="donation purpose"
        onConfirm={handleDelete}
        loading={loading}
      />
    </>
  );
};
